import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import supplierApi, { supplierError } from '../../services/supplierApi';
import {
  EMPTY_SUPPLIER,
  type CreatedSupplier,
  type SupplierCheck,
  type SupplierConfig,
  type SupplierInput,
} from '../../types/suppliers';

/**
 * One new SAP vendor: the form, the live check against SAP (VAT number format, a vendor that already holds
 * the number, same-name vendors) and the Create button. Used by Petty Cash > Suppliers and by the dialog a
 * petty cash line opens for a VAT number SAP does not know. The server checks everything again.
 */

export interface SupplierEditorProps {
  companyId: number;
  config: SupplierConfig;
  initial?: Partial<SupplierInput>;
  source: 'Form' | 'Claim';
  layout: 'page' | 'dialog';
  onCreated: (created: CreatedSupplier) => void;
  onCancel?: () => void;
}

/** The form's starting values: the company's usual group, currency, payment terms and country. */
export function initialSupplier(config: SupplierConfig, initial?: Partial<SupplierInput>): SupplierInput {
  return {
    ...EMPTY_SUPPLIER,
    group: config.defaults.groupCode !== null ? String(config.defaults.groupCode) : '',
    currency: config.defaults.currency ?? '',
    paymentTerms: config.defaults.payTermsCode !== null ? String(config.defaults.payTermsCode) : '',
    country: config.defaults.country ?? '',
    ...Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v !== undefined && v !== null)),
  };
}

type CheckLine = { level: 'ok' | 'error' | 'warning' | 'info'; text: string };

const ICONS = {
  ok: <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} aria-hidden="true" />,
  error: <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} aria-hidden="true" />,
  warning: <WarningAmberIcon fontSize="small" sx={{ color: 'warning.dark' }} aria-hidden="true" />,
  info: <InfoOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} aria-hidden="true" />,
};

/** What the check panel says, from the server's check of the typed VAT number and name. */
export function checkLines(config: SupplierConfig, value: SupplierInput, check: SupplierCheck | null): CheckLine[] {
  const lines: CheckLine[] = [];
  const vat = value.vat.trim();
  if (!vat) {
    lines.push(config.company.vatRegistered
      ? { level: 'error', text: 'The VAT registration number is missing. A shop without one stays on the cash supplier.' }
      : { level: 'info', text: 'No VAT number: allowed for this company.' });
  } else if (check) {
    if (!check.formatOk) lines.push({ level: 'error', text: check.formatMessage || 'The VAT number is not valid' });
    else lines.push({ level: 'ok', text: config.company.vatRule ? `VAT number has the right format (${config.company.vatRule})` : 'VAT number has the right format' });
  }
  if (check?.block) lines.push({ level: 'error', text: check.block });
  else if (check && vat && check.formatOk) lines.push({ level: 'ok', text: `No vendor holds ${check.digits} in SAP (digits compared)` });
  for (const w of check?.warnings || []) lines.push({ level: 'warning', text: w });
  return lines;
}

const SupplierEditor: React.FC<SupplierEditorProps> = ({ companyId, config, initial, source, layout, onCreated, onCancel }) => {
  const [value, setValue] = React.useState<SupplierInput>(() => initialSupplier(config, initial));
  const [check, setCheck] = React.useState<SupplierCheck | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [checkError, setCheckError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const request = React.useRef(0);

  const set = (field: keyof SupplierInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = e.target.value;
    setValue((v) => ({ ...v, [field]: next }));
    setError('');
  };

  // The live check: half a second after the VAT number or the name stops changing.
  const vat = value.vat;
  const name = value.name;
  React.useEffect(() => {
    const digits = vat.replace(/\D/g, '');
    if (!vat.trim() && name.trim().length < 3) { setCheck(null); setChecking(false); return undefined; }
    if (vat.trim() && digits.length < 3 && name.trim().length < 3) { setChecking(false); return undefined; }
    const id = ++request.current;
    setChecking(true);
    const timer = window.setTimeout(() => {
      supplierApi.check(companyId, vat, name)
        .then((res) => { if (id === request.current) { setCheck(res); setCheckError(''); } })
        .catch((e) => { if (id === request.current) setCheckError(supplierError(e, 'SAP could not be checked')); })
        .finally(() => { if (id === request.current) setChecking(false); });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [companyId, vat, name]);

  const lines = checkLines(config, value, check);
  const blocked = !!check?.block || lines.some((l) => l.level === 'error');
  const missingName = !value.name.trim();
  const canCreate = !missingName && !blocked && !checking && !saving && !config.blockingFields.length;

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await supplierApi.create(companyId, value, source);
      onCreated(created);
      if (layout === 'page') {
        setValue(initialSupplier(config));
        setCheck(null);
      }
    } catch (e) {
      setError(supplierError(e, 'The supplier could not be created'));
    } finally {
      setSaving(false);
    }
  };

  const max = (field: keyof SupplierInput) => ({ maxLength: config.maxLength[field] ?? 100 });
  const country = config.countries.find((c) => c.code === value.country) || null;
  const twoCols = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 } as const;
  const section = (title: string) => (
    <Typography component="h3" variant="subtitle2" sx={{ gridColumn: '1 / -1', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>{title}</Typography>
  );

  const form = (
    <Box sx={twoCols}>
      {section('Identity')}
      <TextField label="Supplier name" required value={value.name} onChange={set('name')} inputProps={max('name')} autoFocus={layout === 'dialog'} />
      <TextField label="Arabic name" value={value.foreignName} onChange={set('foreignName')} inputProps={{ ...max('foreignName'), dir: 'auto', lang: 'ar' }} />
      <TextField
        label="VAT registration number"
        required={config.company.vatRegistered}
        value={value.vat}
        onChange={set('vat')}
        inputProps={{ ...max('vat'), inputMode: 'numeric' }}
        helperText={config.company.vatRule || 'Digits only'}
      />
      <TextField
        label="Commercial registration (CRN)"
        value={value.crn}
        onChange={set('crn')}
        inputProps={{ ...max('crn'), inputMode: 'numeric' }}
        helperText={config.company.vatCountry === 'SA' ? '10 digits' : undefined}
      />
      {section('Classification')}
      <TextField select label="Group" value={value.group} onChange={set('group')}>
        {config.groups.map((g) => <MenuItem key={g.code} value={String(g.code)}>{g.name}</MenuItem>)}
      </TextField>
      <TextField select label="Currency" value={value.currency} onChange={set('currency')}>
        {config.currencies.map((c) => <MenuItem key={c.code} value={c.code}>{c.code}{c.name && c.name !== c.code ? ` ${c.name}` : ''}</MenuItem>)}
        <MenuItem value="##">All currencies</MenuItem>
      </TextField>
      <TextField select label="Payment terms" value={value.paymentTerms} onChange={set('paymentTerms')} sx={{ gridColumn: '1 / -1' }}>
        {config.paymentTerms.map((t) => <MenuItem key={t.code} value={String(t.code)}>{t.name}</MenuItem>)}
      </TextField>
      {section('Address (Bill To)')}
      <Autocomplete
        options={config.countries}
        value={country}
        onChange={(_e, c) => setValue((v) => ({ ...v, country: c?.code ?? '' }))}
        getOptionLabel={(c) => c.name}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        renderInput={(params) => (
          <TextField {...params} label="Country" InputLabelProps={{ ...params.InputLabelProps, shrink: country ? true : undefined }} />
        )}
      />
      <TextField label="City" value={value.city} onChange={set('city')} inputProps={max('city')} />
      <TextField label="District" value={value.district} onChange={set('district')} inputProps={max('district')} />
      <TextField label="Street" value={value.street} onChange={set('street')} inputProps={max('street')} />
      <TextField label="Building no." value={value.buildingNo} onChange={set('buildingNo')} inputProps={max('buildingNo')} />
      <TextField label="Postal code" value={value.postalCode} onChange={set('postalCode')} inputProps={max('postalCode')} />
      {section('Contact')}
      <TextField label="Mobile" value={value.mobile} onChange={set('mobile')} inputProps={{ ...max('mobile'), inputMode: 'tel' }} />
      <TextField label="Phone" value={value.phone} onChange={set('phone')} inputProps={{ ...max('phone'), inputMode: 'tel' }} />
      <TextField label="E-mail" type="email" value={value.email} onChange={set('email')} inputProps={max('email')} />
      <TextField label="Contact person" value={value.contactPerson} onChange={set('contactPerson')} inputProps={max('contactPerson')} />
    </Box>
  );

  const checkPanel = (
    <Stack spacing={1} aria-live="polite">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography component="h3" variant="subtitle1" fontWeight={600}>Checked against SAP, live</Typography>
        {checking ? <CircularProgress size={16} aria-label="Checking SAP" /> : null}
      </Stack>
      {lines.length === 0 && !checking ? (
        <Typography variant="body2" color="text.secondary">Type the VAT number and the name: PRPO looks for a vendor that already has them.</Typography>
      ) : null}
      {lines.map((l, i) => (
        <Stack key={`${l.level}-${i}`} direction="row" spacing={1} alignItems="flex-start">
          {ICONS[l.level]}
          <Typography variant="body2">{l.text}</Typography>
        </Stack>
      ))}
      {checkError ? <Alert severity="warning">{checkError}</Alert> : null}
    </Stack>
  );

  const numbering = config.numbering.manual
    ? 'Next number, worked out by PRPO'
    : `Next number of ${config.numbering.seriesName ? `series ${config.numbering.seriesName}` : config.numbering.series !== null ? `series ${config.numbering.series}` : 'the vendor series'}, from SAP`;
  const setByPrpo = (
    <Stack spacing={0.5}>
      <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Set by PRPO</Typography>
      {[
        ['Vendor code', numbering],
        ['VAT status', 'Liable'],
        ['Price list', config.priceListNum !== null ? `Price list ${config.priceListNum}` : 'SAP default'],
        ['Control account', "The vendor group's account in SAP"],
        ['Other SAP fields', 'Their SAP defaults'],
      ].map(([k, v]) => (
        <Stack key={k} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2">{k}</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="right">{v}</Typography>
        </Stack>
      ))}
    </Stack>
  );

  const actions = (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      {onCancel ? <Button variant="outlined" onClick={onCancel} disabled={saving}>Cancel</Button> : null}
      <Button onClick={() => void create()} disabled={!canCreate} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
        {saving ? 'Creating in SAP…' : 'Create supplier in SAP'}
      </Button>
    </Stack>
  );

  const notices = (
    <>
      {config.blockingFields.length ? (
        <Alert severity="error">
          SAP requires {config.blockingFields.join(', ')} on every vendor, which PRPO cannot fill. Create suppliers in SAP B1 until it is given a default.
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </>
  );

  if (layout === 'dialog') {
    return (
      <Stack spacing={2}>
        {form}
        <Paper variant="outlined" sx={{ p: 2 }}>{checkPanel}</Paper>
        {notices}
        {actions}
      </Stack>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
      <Paper variant="outlined" sx={{ flex: '2 1 560px', minWidth: 0, p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">New vendor in {config.company.databaseName}</Typography>
          {form}
          {notices}
          {actions}
        </Stack>
      </Paper>
      <Stack spacing={2} sx={{ flex: '1 1 300px', minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>{checkPanel}</Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>{setByPrpo}</Paper>
      </Stack>
    </Box>
  );
};

export default SupplierEditor;
